import React, { useState } from 'react';
import { Search, Scan, Plus, Clock, Utensils } from 'lucide-react';

interface FoodItem {
  id: string;
  name: string;
  brand?: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  servingSize: string;
  servingUnit: string;
  barcode?: string;
}

interface LoggedFood extends FoodItem {
  quantity: number;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  timestamp: Date;
}

const FoodEntry: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMealType, setSelectedMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>('breakfast');
  const [loggedFoods, setLoggedFoods] = useState<LoggedFood[]>([]);
  const [showScanner, setShowScanner] = useState(false);

  const mockFoodDatabase: FoodItem[] = [
    {
      id: '1',
      name: 'Greek Yogurt',
      brand: 'Chobani',
      calories: 130,
      protein: 20,
      carbs: 6,
      fat: 0,
      servingSize: '1',
      servingUnit: 'cup',
      barcode: '894700010045'
    },
    {
      id: '2',
      name: 'Almonds',
      brand: 'Blue Diamond',
      calories: 160,
      protein: 6,
      carbs: 6,
      fat: 14,
      servingSize: '1',
      servingUnit: 'oz',
      barcode: '041570054321'
    },
    {
      id: '3',
      name: 'Banana',
      calories: 105,
      protein: 1,
      carbs: 27,
      fat: 0,
      servingSize: '1',
      servingUnit: 'medium',
    },
    {
      id: '4',
      name: 'Chicken Breast',
      calories: 165,
      protein: 31,
      carbs: 0,
      fat: 3.6,
      servingSize: '100',
      servingUnit: 'g'
    }
  ];

  const filteredFoods = mockFoodDatabase.filter(food =>
    food.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (food.brand && food.brand.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleAddFood = (food: FoodItem, quantity: number = 1) => {
    const loggedFood: LoggedFood = {
      ...food,
      quantity,
      mealType: selectedMealType,
      timestamp: new Date()
    };
    setLoggedFoods(prev => [...prev, loggedFood]);
  };

  const getTotalNutrition = () => {
    return loggedFoods.reduce(
      (total, food) => ({
        calories: total.calories + (food.calories * food.quantity),
        protein: total.protein + (food.protein * food.quantity),
        carbs: total.carbs + (food.carbs * food.quantity),
        fat: total.fat + (food.fat * food.quantity)
      }),
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    );
  };

  const totalNutrition = getTotalNutrition();

  const getMealTypeIcon = (mealType: string) => {
    switch (mealType) {
      case 'breakfast': return '🌅';
      case 'lunch': return '☀️';
      case 'dinner': return '🌙';
      case 'snack': return '🍎';
      default: return '🍽️';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
          Food Entry
        </h2>
        <button
          onClick={() => setShowScanner(!showScanner)}
          className="p-3 rounded-lg transition-all duration-200"
          style={{
            background: showScanner ? 'var(--gradient-primary)' : 'var(--gradient-card-bg)',
            color: showScanner ? 'var(--color-white)' : 'var(--color-text-muted)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <Scan className="h-5 w-5" />
        </button>
      </div>

      <div className="bg-white rounded-xl p-6" style={{ boxShadow: 'var(--shadow-lg)' }}>
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
            Select Meal Type
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((mealType) => (
              <button
                key={mealType}
                onClick={() => setSelectedMealType(mealType)}
                className="p-3 rounded-lg transition-all duration-200 text-center min-h-[80px] flex flex-col items-center justify-center"
                style={{
                  background: selectedMealType === mealType 
                    ? 'var(--gradient-primary)' 
                    : 'var(--gradient-card-bg)',
                  color: selectedMealType === mealType 
                    ? 'var(--color-white)' 
                    : 'var(--color-text)',
                  boxShadow: 'var(--shadow-sm)'
                }}
              >
                <div className="text-xl mb-1">{getMealTypeIcon(mealType)}</div>
                <span className="text-xs font-medium capitalize leading-tight">{mealType}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
            <input
              type="text"
              placeholder="Search for foods..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border text-base"
              style={{
                borderColor: 'rgba(0,0,0,0.1)',
                background: 'var(--color-white)'
              }}
            />
          </div>
        </div>

        {searchQuery && (
          <div className="mb-6">
            <h4 className="text-base font-semibold mb-3" style={{ color: 'var(--color-text)' }}>
              Search Results
            </h4>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {filteredFoods.map((food) => (
                <div
                  key={food.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:shadow-md transition-all duration-200"
                  style={{ borderColor: 'rgba(0,0,0,0.08)' }}
                >
                  <div className="flex-1">
                    <h5 className="font-medium" style={{ color: 'var(--color-text)' }}>
                      {food.name}
                    </h5>
                    {food.brand && (
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        {food.brand}
                      </p>
                    )}
                    <div className="flex items-center space-x-4 mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      <span>{food.calories} cal</span>
                      <span>{food.protein}g protein</span>
                      <span>per {food.servingSize} {food.servingUnit}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAddFood(food)}
                    className="p-2 rounded-lg transition-all duration-200"
                    style={{
                      background: 'var(--gradient-primary)',
                      color: 'var(--color-white)',
                      boxShadow: 'var(--shadow-sm)'
                    }}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              ))}
              {filteredFoods.length === 0 && (
                <p className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
                  No foods found. Try a different search term.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {loggedFoods.length > 0 && (
        <div className="bg-white rounded-xl p-6" style={{ boxShadow: 'var(--shadow-lg)' }}>
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
            Today's Food Log
          </h3>
          
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(58, 175, 169, 0.1)' }}>
              <span className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
                {Math.round(totalNutrition.calories)}
              </span>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Calories</p>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(156, 106, 222, 0.1)' }}>
              <span className="text-xl font-bold" style={{ color: 'var(--color-secondary)' }}>
                {Math.round(totalNutrition.protein)}g
              </span>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Protein</p>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(244, 182, 194, 0.1)' }}>
              <span className="text-xl font-bold" style={{ color: 'var(--color-accent)' }}>
                {Math.round(totalNutrition.carbs)}g
              </span>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Carbs</p>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ background: 'rgba(163, 177, 138, 0.1)' }}>
              <span className="text-xl font-bold" style={{ color: 'var(--color-sage-green)' }}>
                {Math.round(totalNutrition.fat)}g
              </span>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Fat</p>
            </div>
          </div>

          <div className="space-y-3">
            {loggedFoods.map((food, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 rounded-lg"
                style={{ background: 'var(--gradient-card-bg)' }}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-xl">{getMealTypeIcon(food.mealType)}</span>
                  <div>
                    <h5 className="font-medium" style={{ color: 'var(--color-text)' }}>
                      {food.name} {food.brand && `(${food.brand})`}
                    </h5>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      {food.quantity} × {food.servingSize} {food.servingUnit}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium" style={{ color: 'var(--color-text)' }}>
                    {Math.round(food.calories * food.quantity)} cal
                  </p>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    {Math.round(food.protein * food.quantity)}g protein
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodEntry;
